import { Sparkles, ArrowRight } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';

interface PlaceholderPageProps {
  title: string;
  // Optional one-liner describing what the future page will do. Keeps the
  // copy specific so the placeholder reads as "coming soon, here's what
  // it will be" rather than a stray under-construction sign.
  description?: string;
}

// Polished placeholder used by every sidebar destination that hasn't
// shipped yet (Dashboard, Portfolio, Execution, Administration, Soteria
// AI, Workspace, Clients, Settings, Docs, Help Desk). The earlier version
// dropped a generic hard-hat icon on a blank canvas, which looked broken
// next to the rest of the polished surfaces. This one renders a centered
// card with a soft gradient blob behind the icon, the page title + a
// short context line, and a clear path back to the strategies hub so a
// demo viewer always has an obvious next click.
export const PlaceholderPage = ({ title, description }: PlaceholderPageProps) => {
  const navigate = useNavigate();
  const blurb =
    description ??
    `${title} is on the roadmap. The strategy hub is the polished surface to walk through today, the rest of the platform lights up over the coming releases.`;

  return (
    <div className="flex items-center justify-center min-h-[calc(100vh-10rem)] px-6 animate-in fade-in duration-500">
      <div className="relative w-full max-w-xl">
        {/* Soft brand-tinted blobs sitting behind the card. Pure decoration,
            kept aria-hidden so screen readers skip them. */}
        <div
          aria-hidden
          className="absolute -top-12 -left-12 h-40 w-40 rounded-full bg-gradient-to-br from-amber-200/60 via-amber-100/40 to-transparent blur-3xl"
        />
        <div
          aria-hidden
          className="absolute -bottom-16 -right-10 h-44 w-44 rounded-full bg-gradient-to-tr from-muted via-muted/40 to-transparent blur-3xl"
        />

        <div className="relative rounded-2xl border border-border bg-card shadow-sm p-10 text-center">
          <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-gradient-to-br from-amber-50 to-amber-100 ring-1 ring-amber-200/60 mb-5">
            <Sparkles className="h-7 w-7 text-amber-600" />
          </div>

          <p className="text-xs font-medium uppercase tracking-[0.18em] text-amber-700 mb-2">
            Coming soon
          </p>
          <h1 className="text-2xl font-semibold text-foreground mb-3">{title}</h1>
          <p className="text-sm leading-relaxed text-muted-foreground mb-7">{blurb}</p>

          <button
            type="button"
            onClick={() => navigate({ to: '/strategies' })}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            Back to Strategies
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
